import { Edit, Lock, LockOpen } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { debounce, isEmpty, isNil } from "lodash";
import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi, useApiMutation } from "../hooks/useApi";
import { Client } from "../types/client";
import { Profile, SystemUser } from "../types/contact";
import PromoteDialog from "./PromoteDialog";
import UnlockDialog from "./UnlockDialog";

interface Props {}

const AdminData: FunctionComponent<Props> = (props: Props): JSX.Element => {
  const { data: profile } = useApi<Profile>("profile");
  const [filteredClients, setFilteredClients] = useState<Client[] | undefined>(
    profile?.results?.clients?.filter((client) => client.isPrimary)
  );
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [unlockOpen, setUnlockOpen] = useState<boolean>(false);
  const [promoteOpen, setPromoteOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SystemUser | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchRef = useRef<HTMLInputElement>();
  const [promoted, setPromoted] = useState<boolean>(false);
  const { data: systemusersQuery, isLoading } = useApi<SystemUser[]>(
    "systemusers",
    { clientId: selectedClientId, search: searchQuery },
    {
      count: rowsPerPage,
      page: page + 1,
    },
    !isNil(selectedClientId)
  );

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { apiUpdate } = useApiMutation();

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const { t } = useTranslation();

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    item: SystemUser
  ) => {
    setSelectedItem(item);
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorElUser(null);
  };

  const handleUnlockOpen = () => {
    setUnlockOpen(true);
  };
  const handleUnlockClose = () => {
    setUnlockOpen(false);
    setSelectedItem(null);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, rowsPerPage));
    setPage(0);
  };

  const handlePromoteOpen = () => {
    setPromoteOpen(true);
    // setUnlockOpen(false)
  };
  const handlePromoteClose = () => {
    setPromoteOpen(false);
  };

  const handleUnlockClick = (selectedItem: SystemUser | null) => {
    apiUpdate.mutate(
      {
        entity: "systemusers",
        post: {
          id: selectedItem?.uid,
          isLocked: false,
          uid: selectedItem?.uid,
        },
      },
      {
        onError: (error) => console.error(error),
      }
    );
  };

  const handleLockClick = (selectedItem: SystemUser | null) => {
    apiUpdate.mutate(
      {
        entity: "systemusers",
        post: {
          id: selectedItem?.uid,
          isLocked: true,
          uid: selectedItem?.uid,
        },
      },
      {
        onError: (error) => console.error(error),
      }
    );
  };

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedClientId(event.target.value);
    setPage(0);
  };

  useEffect(() => {
    if (profile?.results?.clients && profile.results.clients.length > 0) {
      const filteredClients = profile?.results?.clients?.filter(
        (client) => client.isPrimary
      );
      setFilteredClients(filteredClients);
      if (filteredClients) {
        setSelectedClientId(filteredClients[0].id);
      }
    }
  }, [profile]);

  return (
    <Stack spacing={4} padding={isSmallScreen ? 0 : 1}>
      <TableContainer>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          paddingTop={1}
          paddingBottom={2}
        >
          {filteredClients && filteredClients.length > 1 ? (
            selectedClientId && (
              <FormControl sx={{ width: "30%" }}>
                <InputLabel size="small"> {t("adminPage.client")} </InputLabel>
                <Select
                  size="small"
                  value={selectedClientId}
                  label={t("adminPage.client")}
                  onChange={handleChange}
                >
                  {filteredClients.map((client) => (
                    <MenuItem key={`clientList-${client.id}`} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          ) : (
            <Typography variant="h6" sx={{ m: "10px 0 10px 0" }}>
              {filteredClients && filteredClients[0].name}{" "}
            </Typography>
          )}

          <FormControl variant="outlined" sx={{ width: "30%" }}>
            <InputLabel size="small">{t("adminPage.search")}</InputLabel>
            <OutlinedInput
              inputRef={searchRef}
              size="small"
              onChange={debounce(
                (event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchQuery(event.target.value);
                },
                1000
              )}
              type="text"
              endAdornment={
                !isEmpty(searchQuery) && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchQuery("");
                        searchRef.current!.value = "";
                      }}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }
              label={t("adminPage.search")}
            />
          </FormControl>
        </Stack>

        {!isLoading ? (
          <>
            {systemusersQuery?.results &&
            systemusersQuery?.results?.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("adminTable.name")}</TableCell>
                    <TableCell>{t("profilePage.email")}</TableCell>
                    <TableCell>{t("adminTable.timesheetAccess")}</TableCell>
                    <TableCell>{t("adminTable.status")}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {systemusersQuery?.results?.map((item, index) => (
                    <TableRow key={`systemuser-${item.uid}`}>
                      <TableCell>
                        <Stack
                          direction={"row"}
                          alignContent={"center"}
                          spacing={2}
                        >
                          {item.isPrimary && (
                            <Tooltip title={t("tooltips.primaryContact")} arrow>
                              <StarsRoundedIcon
                                fontSize="small"
                                sx={{ color: "#005292" }}
                              />
                            </Tooltip>
                          )}
                          <Typography variant="body2">{item.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell> {item.email} </TableCell>
                      <TableCell>
                        {item.accessLevel === 2 || item.isPrimary ? (
                          <Chip label={t("adminTable.all")} color="warning" />
                        ) : (
                          <Chip label={t("adminTable.own")} color="default" />
                        )}{" "}
                      </TableCell>
                      <TableCell>
                        {item.isLocked === false ? (
                          <Chip
                            label={t("adminTable.active")}
                            color="success"
                          />
                        ) : (
                          <Chip label={t("adminTable.locked")} color="error" />
                        )}{" "}
                      </TableCell>

                      <TableCell>
                        <Tooltip title={t("tooltips.options")}>
                          <IconButton
                            aria-label="more"
                            size="small"
                            onClick={(event) => handleOpenMenu(event, item)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          anchorEl={anchorElUser}
                          id="account-menu"
                          open={Boolean(anchorElUser)}
                          onClose={handleCloseMenu}
                          onClick={handleCloseMenu}
                          slotProps={{
                            paper: {
                              elevation: 0,
                              sx: {
                                overflow: "visible",
                                filter:
                                  "drop-shadow(0px 2px 8px rgba(0,0,0,0.08))",
                                mt: 1.5,
                                "& .MuiAvatar-root": {
                                  width: 32,
                                  height: 32,
                                  ml: -0.5,
                                  mr: 1,
                                },
                                "&::before": {
                                  content: '""',
                                  display: "block",
                                  position: "absolute",
                                  top: 0,
                                  right: 14,
                                  width: 10,
                                  height: 10,
                                  bgcolor: "background.paper",
                                  transform: "translateY(-50%) rotate(45deg)",
                                  zIndex: 0,
                                },
                              },
                            },
                          }}
                          transformOrigin={{
                            horizontal: "right",
                            vertical: "top",
                          }}
                          anchorOrigin={{
                            horizontal: "right",
                            vertical: "bottom",
                          }}
                        >
                          {selectedItem?.isLocked ? (
                            <MenuItem
                              onClick={() => handleUnlockClick(selectedItem)}
                              key={"menuItem-isLocked"}
                            >
                              <ListItemIcon>
                                <LockOpen fontSize="small" />
                              </ListItemIcon>
                              {t("adminTable.unlock")}
                            </MenuItem>
                          ) : (
                            <MenuItem
                              onClick={() => handleLockClick(selectedItem)}
                              key={"menuItem-unlocked"}
                            >
                              <ListItemIcon>
                                <Lock fontSize="small" />
                              </ListItemIcon>
                              {t("adminTable.lock")}
                            </MenuItem>
                          )}

                          <MenuItem
                            onClick={handleUnlockOpen}
                            key={"menuItem-edit"}
                          >
                            <ListItemIcon>
                              <Edit fontSize="small" />
                            </ListItemIcon>
                            {t("adminTable.edit")}
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[10]}
                      colSpan={5}
                      count={systemusersQuery?.paging?.totalCount ?? 0}
                      rowsPerPage={rowsPerPage}
                      page={page ?? 0}
                      slotProps={{
                        select: {
                          inputProps: {
                            "aria-label": "rows per page",
                          },
                          native: true,
                        },
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                      labelDisplayedRows={({ from, to, count }) =>
                        t("tsTable.paginationText", { from, to, count })
                      }
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            ) : (
              <Typography sx={{ m: "10px 0 10px 0" }}>
                {t("adminTable.noResults")}
              </Typography>
            )}
          </>
        ) : (
          <Box paddingTop={4} display="flex" justifyContent="center">
            <CircularProgress size={"50px"} />
          </Box>
        )}

        <UnlockDialog
          handleUnlockOpen={handleUnlockOpen}
          handleUnlockClose={handleUnlockClose}
          handlePromoteOpen={handlePromoteOpen}
          unlockOpen={unlockOpen}
          setUnlockOpen={setUnlockOpen}
          selectedItem={selectedItem}
          promoted={promoted}
          setPromoted={setPromoted}
          // switchChecked={switchChecked}
          // setSwitchChecked={setSwitchChecked}
        />

        <PromoteDialog
          handlePromoteOpen={handlePromoteOpen}
          handlePromoteClose={handlePromoteClose}
          promoteOpen={promoteOpen}
          setPromoteOpen={setPromoteOpen}
          selectedItem={selectedItem}
          setPromoted={setPromoted}
        />
      </TableContainer>
    </Stack>
  );
};

export default AdminData;
